import { pipe } from "fp-ts/lib/function"

import { doSegmentsIntersect, isSamePoint } from "."
import type { Point2d } from ".."
import type { Edge2dComputed } from "../../mesh"
import { isPointOnSegment } from "../collision/isPointOnSegment"
import { findMidpoint } from "../findMidpoint"
import { findSegmentsClosestPoints } from "../findSegmentsClosestPoints"
import { edgeToLinearFn } from "../transmute/edgeToLinearFn"

const { isFinite } = Number

export const whatPointDoSegmentsShare = (
  e1: Edge2dComputed,
  e2: Edge2dComputed,
): Point2d | null => {
  const { p1: a, p2: b } = e1
  const { p1: c, p2: d } = e2
  if (isSamePoint(a, c) || isSamePoint(a, d)) {
    return a
  }
  if (isSamePoint(b, c) || isSamePoint(b, d)) {
    return b
  }

  return null
}

export type WhereDoSegmentsIntersect = (
  e1: Edge2dComputed,
  e2: Edge2dComputed,
  options?: {
    allowExtension?: boolean
    inclusiveMode?: boolean
    logging?: boolean
  },
) => Point2d | null

export const whereDoSegmentsIntersect: WhereDoSegmentsIntersect = (
  e1,
  e2,
  { allowExtension = false, inclusiveMode = true, logging = false } = {
    allowExtension: false,
    inclusiveMode: true,
    logging: false,
  },
) => {
  const sharedPoint = whatPointDoSegmentsShare(e1, e2)
  if (sharedPoint) {
    return inclusiveMode ? sharedPoint : null
  }

  if (allowExtension === false && doSegmentsIntersect(e1, e2) === false) {
    return null
  }

  const { p1: a, p2: b } = e1
  const { p1: c, p2: d } = e2

  const fn1 = edgeToLinearFn(e1)
  const fn2 = edgeToLinearFn(e2)

  const differenceBetweenSlopes = fn1.slope - fn2.slope
  if (
    Math.abs(differenceBetweenSlopes) < 0.0000001 ||
    Number.isNaN(differenceBetweenSlopes)
  ) {
    const innerPoints: Point2d[] = []
    isPointOnSegment(a, e2) && innerPoints.push(a)
    isPointOnSegment(b, e2) && innerPoints.push(b)
    isPointOnSegment(c, e1) && innerPoints.push(c)
    isPointOnSegment(d, e1) && innerPoints.push(d)
    if (logging) {
      console.log(`Segments are parallel; ${innerPoints.length} overlap`)
    }
    switch (innerPoints.length) {
      case 2:
        return inclusiveMode
          ? {
              x: (innerPoints[0].x + innerPoints[1].x) / 2,
              y: (innerPoints[0].y + innerPoints[1].y) / 2,
            }
          : null
      default:
        // console.debug(`Segments are parallel; ${innerPoints.length} overlap`)
        return null
    }
  }

  const verticalSegment = !isFinite(fn1.slope)
    ? e1
    : !isFinite(fn2.slope)
    ? e2
    : undefined
  if (verticalSegment) {
    const otherFn = verticalSegment === e1 ? fn2 : fn1,
      { x } = verticalSegment.p1,
      y = otherFn(x)

    return { x, y }
  }

  // fn1.slope * x + fn1.intercept = fn2.slope * x + fn2.intercept
  const x = (fn2.intercept - fn1.intercept) / (fn1.slope - fn2.slope)
  const y = fn1(x)

  const intersection = { x, y }

  if (logging) {
    console.log({
      e1,
      e2,
      fn1,
      fn2,
      differenceBetweenSlopes,
      x,
      y,
      intersection,
      isOn: {
        e1: isPointOnSegment({ x, y }, e1),
        e2: isPointOnSegment({ x, y }, e2),
      },
    })
  }

  return intersection
}

export const whereWouldSegmentsIntersect = (
  e1: Edge2dComputed,
  e2: Edge2dComputed,
): Point2d =>
  whereDoSegmentsIntersect(e1, e2, { allowExtension: true }) ??
  pipe({ e1, e2 }, findSegmentsClosestPoints, findMidpoint)
